import { createCompanyBody, type UserListItem } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { ImageIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCompaniesContent } from '../../content/sections/companies.js';
import { useCreateCompany, usePublisherSlugAvailable } from '../../hooks/use-companies.js';
import { UserPicker } from '../users/user-picker.js';
import { companyErrorMessage } from './company-error.js';

const slugField = createCompanyBody.shape.publisherSlug;

/* Validação client da logo (ADR-0615): só p/ preview + feedback imediato — a
   autoridade é o auth-service (§4.15). Espelha a allowlist/teto do avatar. */
const LOGO_ALLOWED_MIME = ['image/png', 'image/jpeg', 'image/webp'];
const LOGO_MAX_BYTES = 524_288;

/** Lê um File como data URL base64. Rejeita se o FileReader falhar. */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });
}

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

/* Form de criação de org admin-created (C1 slice 3, ADR-0421): organizações deixam
   de ser self-serve. O admin nomeia um owner inicial (usuário EXISTENTE, via
   UserPicker). Sucesso → navega ao detalhe da nova org (onde o plano enterprise é
   atribuído em C6). `DialogContent locked` durante a mutation (ADR-0216). */
export function CreateCompanyModal({ open, onClose }: Props) {
  const copy = useCompaniesContent();
  const navigate = useNavigate();
  const create = useCreateCompany();
  const [name, setName] = useState('');
  const [publisherSlug, setPublisherSlug] = useState('');
  const [owner, setOwner] = useState<UserListItem | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setName('');
      setPublisherSlug('');
      setOwner(null);
      setLogoDataUrl(null);
      setLogoError(null);
    }
  }, [open]);

  const onLogoChange = (file: File | null): void => {
    setLogoError(null);
    if (file === null) {
      setLogoDataUrl(null);
      return;
    }
    if (!LOGO_ALLOWED_MIME.includes(file.type)) {
      setLogoDataUrl(null);
      setLogoError(copy.createModal.logoInvalidFormat);
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLogoDataUrl(null);
      setLogoError(copy.createModal.logoTooLarge);
      return;
    }
    readFileAsDataUrl(file)
      .then(setLogoDataUrl)
      .catch(() => setLogoError(copy.createModal.logoReadError));
  };

  const normalizedSlug = publisherSlug.trim().toLowerCase();
  const input = {
    name,
    ownerUserId: owner?.id ?? '',
    publisherSlug: normalizedSlug,
    ...(logoDataUrl !== null && { logoDataUrl }),
  };
  const valid = createCompanyBody.safeParse(input).success;
  const slugFormatValid = slugField.safeParse(normalizedSlug).success;
  const slugState = usePublisherSlugAvailable(normalizedSlug, slugFormatValid);
  const submitDisabled =
    !valid || slugState === 'checking' || slugState === 'taken' || logoError !== null;

  const submit = (): void => {
    create.mutate(input, {
      onSuccess: (org) => {
        toast.success(copy.createModal.success(org.name));
        onClose();
        navigate({ to: '/companies/$id', params: { id: org.id } }).catch(() => undefined);
      },
      onError: (err) => toast.error(companyErrorMessage(err, copy.errors)),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !create.isPending) onClose();
      }}
    >
      <DialogContent locked={create.isPending}>
        <DialogHeader>
          <DialogTitle>{copy.createModal.title}</DialogTitle>
          <DialogDescription>{copy.createModal.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cc-name">{copy.createModal.nameLabel}</Label>
            <Input
              id="cc-name"
              value={name}
              placeholder={copy.createModal.namePlaceholder}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-publisher-slug">{copy.createModal.slugLabel}</Label>
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">@</span>
              <Input
                id="cc-publisher-slug"
                value={publisherSlug}
                placeholder={copy.createModal.slugPlaceholder}
                onChange={(e) => setPublisherSlug(e.target.value)}
              />
            </div>
            <p className="text-xs text-muted-foreground">{copy.createModal.slugHelp}</p>
            {slugState === 'checking' && (
              <p className="text-xs text-muted-foreground">{copy.createModal.slugChecking}</p>
            )}
            {slugState === 'available' && (
              <p className="text-xs text-success">{copy.createModal.slugAvailable}</p>
            )}
            {slugState === 'taken' && (
              <p className="text-xs text-destructive">{copy.createModal.slugTaken}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-logo">{copy.createModal.logoLabel}</Label>
            <div className="flex items-center gap-3">
              <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
                {logoDataUrl !== null ? (
                  <img
                    src={logoDataUrl}
                    alt={copy.createModal.logoPreviewAlt}
                    className="size-full object-cover"
                  />
                ) : (
                  <ImageIcon className="size-5 text-muted-foreground" aria-hidden="true" />
                )}
              </span>
              <Input
                id="cc-logo"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => onLogoChange(e.target.files?.[0] ?? null)}
              />
            </div>
            <p className="text-xs text-muted-foreground">{copy.createModal.logoHelp}</p>
            {logoError !== null && <p className="text-xs text-destructive">{logoError}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-owner">{copy.createModal.ownerLabel}</Label>
            <UserPicker
              value={owner}
              onChange={setOwner}
              placeholder={copy.createModal.ownerPlaceholder}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            {copy.createModal.cancel}
          </Button>
          <Button loading={create.isPending} disabled={submitDisabled} onClick={submit}>
            {copy.createModal.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
